import { Dimensions, Metrics, type OverviewResponse } from '@/api/types';
import { metricValue } from '@/api/metric-resolution';
import { BarList } from '@/components/BarList';
import { ExportButton } from '@/components/ExportButton';
import { RangeBar } from '@/components/RangeBar';
import { breakdown, toBars, useOverview } from '@/hooks/useOverview';
import { AnalyticsLoadError, noSourceSentence } from '@/components/AnalyticsLoadError';

/**
 * Where visitors come from, and what they arrive on.
 *
 * Every panel is a horizontal bar list in ONE hue: these are all the same measure (sessions)
 * sliced by a dimension, so giving each row its own colour would imply an identity that is not
 * there — and would burn the categorical slots for nothing.
 */
export function AcquisitionPage({ siteKey }: { siteKey: string }) {
  const { data, isLoading, isError, error, days, setDays, from, to, setRange, isCustomRange } = useOverview(siteKey);

  const panels = [
    { title: 'Channels', dimension: Dimensions.channelGroup },
    { title: 'Sources', dimension: Dimensions.source },
    { title: 'Mediums', dimension: Dimensions.medium },
    { title: 'Campaigns', dimension: Dimensions.campaign },
    { title: 'Referrers', dimension: Dimensions.referrer },
    { title: 'Landing pages', dimension: Dimensions.landingPage },
  ];

  const audience = [
    { title: 'Countries', dimension: Dimensions.country },
    { title: 'Cities', dimension: Dimensions.city },
    // For an Arabic-first site aimed at Egypt this is the single most informative
    // breakdown on the page: it says whether the English version is used at all.
    { title: 'Languages', dimension: Dimensions.language },
    { title: 'New vs returning', dimension: Dimensions.newVsReturning },
  ];

  const tech = [
    { title: 'Devices', dimension: Dimensions.device },
    { title: 'Browsers', dimension: Dimensions.browser },
    { title: 'Operating systems', dimension: Dimensions.operatingSystem },
  ];

  const anyData = [...panels, ...audience, ...tech].some(
    (p) => breakdown(data, p.dimension).length > 0,
  );

  return (
    <>
      <RangeBar
        days={days} setDays={setDays} from={from} to={to} data={data}
        setRange={setRange} isCustomRange={isCustomRange}
        actions={
          <ExportButton
            siteKey={siteKey} from={from} to={to}
            dimension={Dimensions.channelGroup} label="Export channels"
          />
        }
      />

      {isLoading && <div className="empty-state"><span className="spinner" /></div>}
      {isError && <AnalyticsLoadError error={error} what="acquisition" notFound={noSourceSentence(siteKey)} />}

      {data && !anyData && (
        <div className="empty-state t-body">
          No per-dimension rows yet — expected before the tags are live.
          <div className="t-label subtle" style={{ marginTop: 4 }}>
            If this persists once traffic exists, the API log names the setting to change.
          </div>
        </div>
      )}

      {data && anyData && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
            {panels.map((panel) => (
              <Panel key={panel.dimension} title={panel.title} rows={toBars(breakdown(data, panel.dimension))} />
            ))}
          </div>

          <h2 className="t-heading" style={{ margin: '8px 0 0' }}>Audience</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {audience.map((panel) => (
              <Panel key={panel.dimension} title={panel.title} rows={toBars(breakdown(data, panel.dimension))} />
            ))}
          </div>

          <h2 className="t-heading" style={{ margin: '8px 0 0' }}>Technology</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {tech.map((panel) => (
              <Panel key={panel.dimension} title={panel.title} rows={toBars(breakdown(data, panel.dimension))} />
            ))}
          </div>

          <ConversionByChannel data={data} />
        </>
      )}
    </>
  );
}

/**
 * Sessions vs LEADS per campaign — the table that answers "which campaign actually paid off",
 * rather than which one merely sent clicks.
 *
 * A table rather than a chart on purpose: three numbers per row where two are on completely
 * different scales (thousands of sessions, single-digit leads) has no honest shared axis, and a
 * dual-axis chart is the one thing the visualisation rules forbid outright.
 */
function ConversionByChannel({ data }: { data: OverviewResponse }) {
  const sessionRows = breakdown(data, Dimensions.campaign);
  const leadRows = breakdown(data, Dimensions.campaign);

  const leadsByValue = new Map<string, number>();
  leadRows.forEach((row) => {
    const leads = row.metrics[Metrics.leads];
    if (leads !== undefined) leadsByValue.set(row.value, leads);
  });

  if (leadsByValue.size === 0) return null;

  const rows = sessionRows
    .map((row) => {
      const sessions = metricValue(row.metrics, 'sessions') ?? 0;
      const leads = leadsByValue.get(row.value) ?? 0;
      return { label: row.value || '(none)', sessions, leads };
    })
    .filter((row) => row.sessions > 0 || row.leads > 0)
    .sort((a, b) => b.leads - a.leads)
    .slice(0, 20);

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Campaign performance</h2>
        <span className="t-label subtle">first-touch attribution</span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Campaign</th>
            <th className="num">Sessions</th>
            <th className="num">Leads</th>
            <th className="num">Rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              <td className="num">{row.sessions.toLocaleString('en-US')}</td>
              <td className="num">{row.leads.toLocaleString('en-US')}</td>
              <td className="num">
                {row.sessions > 0 ? `${((row.leads / row.sessions) * 100).toFixed(2)}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Panel({ title, rows }: { title: string; rows: { label: string; value: number }[] }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
        <span className="t-label subtle">{rows.length} value{rows.length === 1 ? '' : 's'}</span>
      </div>
      <div className="card-body">
        <BarList rows={rows} limit={10} emptyLabel="Not reported by the connected providers yet." />
      </div>
    </div>
  );
}
